import { useInstantSearch } from 'react-instantsearch';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { So5FixtureEvent } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { FilterTitle } from '@sorare/core/src/components/search/FilterTitle';
import { useFacet } from '@sorare/core/src/contexts/graphql-search';
import { FILTERS, type FilterWidget } from '@sorare/core/src/lib/filters';
import type { ProjectionDifference } from '@sorare/core/src/lib/scouting-center';

import {
  BackendRangeSlider,
  RangeSlider,
} from '#src/search/RangeSlider/index.tsx';
import { makeRangeFilter } from '#src/searchCards/RangeSlider/index.tsx';

const projectionsFilters = {
  [So5FixtureEvent.CLASSIC]: {
    score: FILTERS.projectedScoreClassic,
    difference: FILTERS.gainOnClassicProjection,
  },
  [So5FixtureEvent.DAILY]: {
    score: FILTERS.projectedScoreDaily,
    difference: FILTERS.gainOnDailyProjection,
  },
};

const backendProjectionUrlKeys = {
  [So5FixtureEvent.CLASSIC]: {
    score: FILTERS.projectedScoreClassic.urlAttribute,
    difference: FILTERS.gainOnClassicProjection.urlAttribute,
  },
  [So5FixtureEvent.DAILY]: {
    score: FILTERS.projectedScoreDaily.urlAttribute,
    difference: FILTERS.gainOnDailyProjection.urlAttribute,
  },
} as const;

const ProjectionTitle = ({ mode }: { mode: ProjectionDifference }) => {
  const { indexUiState } = useInstantSearch();
  const selected =
    (indexUiState.range?.[projectionsFilters[mode].score.attribute] ? 1 : 0) +
    (indexUiState.range?.[projectionsFilters[mode].difference.attribute]
      ? 1
      : 0);

  return (
    <FilterTitle
      name={projectionsFilters[mode].score.title}
      selected={selected}
    />
  );
};

const Section = styled(Vertical)`
  &:not(:has(.FilterWidget.visible)) {
    display: none;
  }
`;

const ProjectionComponent = ({
  mode,
  scoreWidget,
  differenceWidget,
  visible,
}: {
  mode: ProjectionDifference;
  scoreWidget: FilterWidget;
  differenceWidget: FilterWidget;
  visible: boolean;
}) => {
  return (
    <Vertical gap={3}>
      <Section>
        <LabelM color="var(--c-nd-600)">
          <FormattedMessage {...projectionsFilters[mode].score.title} />
        </LabelM>
        <scoreWidget.Component visible={visible} {...scoreWidget.props} />
      </Section>
      <Section>
        <LabelM color="var(--c-nd-600)">
          <FormattedMessage {...projectionsFilters[mode].difference.title} />
        </LabelM>
        <differenceWidget.Component
          visible={visible}
          {...differenceWidget.props}
        />
      </Section>
    </Vertical>
  );
};

const RefineProjection = (mode: ProjectionDifference): FilterWidget => {
  const { score, difference } = projectionsFilters[mode];
  const scoreWidget = makeRangeFilter(
    ({ visible }) => (
      <RangeSlider attribute={score.attribute} visible={visible} />
    ),
    score
  );
  const differenceWidget = makeRangeFilter(
    ({ visible }) => (
      <RangeSlider attribute={difference.attribute} visible={visible} />
    ),
    difference
  );

  return {
    key: score.attribute,
    type: 'range',
    attribute: score.attribute,
    title: <ProjectionTitle mode={mode} />,
    Component: ({ visible }) => (
      <ProjectionComponent
        mode={mode}
        scoreWidget={scoreWidget}
        differenceWidget={differenceWidget}
        visible={visible}
      />
    ),
  };
};

export const RefineDailyProjection = RefineProjection(So5FixtureEvent.DAILY);
export const RefineClassicProjection = RefineProjection(
  So5FixtureEvent.CLASSIC
);

const BackendProjectionTitle = ({ mode }: { mode: ProjectionDifference }) => {
  const { score: scoreKey, difference: differenceKey } =
    backendProjectionUrlKeys[mode];
  const { selected: selectedScore } = useFacet(scoreKey);
  const { selected: selectedDifference } = useFacet(differenceKey);

  return (
    <FilterTitle
      name={projectionsFilters[mode].score.title}
      selected={[selectedScore, selectedDifference].filter(Boolean).length}
    />
  );
};

const BackendProjectionComponent = ({
  mode,
  visible,
}: {
  mode: ProjectionDifference;
  visible: boolean;
}) => {
  const { score: scoreKey, difference: differenceKey } =
    backendProjectionUrlKeys[mode];
  const { score, difference } = projectionsFilters[mode];

  return (
    <Vertical gap={3}>
      <Section>
        <LabelM color="var(--c-nd-600)">
          <FormattedMessage {...score.title} />
        </LabelM>
        <BackendRangeSlider attribute={scoreKey} visible={visible} />
      </Section>
      <Section>
        <LabelM color="var(--c-nd-600)">
          <FormattedMessage {...difference.title} />
        </LabelM>
        <BackendRangeSlider attribute={differenceKey} visible={visible} />
      </Section>
    </Vertical>
  );
};

const RefineBackendProjection = (mode: ProjectionDifference): FilterWidget => {
  const { score: scoreKey } = backendProjectionUrlKeys[mode];

  return {
    key: scoreKey,
    type: 'range',
    attribute: scoreKey,
    title: <BackendProjectionTitle mode={mode} />,
    Component: ({ visible }) => (
      <BackendProjectionComponent mode={mode} visible={visible} />
    ),
  };
};

export const RefineBackendDailyProjection = RefineBackendProjection(
  So5FixtureEvent.DAILY
);
export const RefineBackendClassicProjection = RefineBackendProjection(
  So5FixtureEvent.CLASSIC
);
